import {EventEmitter} from "@/modules/base/events";
import {BeforeCreateGameEvent} from "@/modules/base/events/BeforeCreateGameEvent";
import {AfterCreateGameEvent} from "@/modules/base/events/AfterCreateGameEvent";
import {inject, injectable} from "inversify";
import {Season} from "@/modules/season/entity";
import CountryCompetitionFactory from "@/modules/competitions/services/factories/CountryCompetitionFactory";
import WorldFactory from "@/modules/base/services/factories/WorldFactory";
import {nonblocking} from "@/utils";
import {Country} from "@/modules/base/entity";
import {Team} from "@/modules/team/entity";
import TeamFactory from "@/modules/team/services/factories/TeamFactory";
import SettingRepository from "@/modules/settings/services/SettingRepository";
import chance from "@/modules/base/services/chance";
import {CountryEnum} from "@/modules/base/enums";
import {persistDataSource} from "@/modules/database/services/db";
import {db} from "@/modules/base/services/db";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import FastPlayerFactory from "@/modules/team/services/factories/FastPlayerFactory";
import {SectionLoader} from "@/modules/base/services/SectionLoader";
import BeforeGameLoadedEvent from "@/modules/main-menu/events/BeforeGameLoadedEvent";
import AfterGameLoadedEvent from "@/modules/main-menu/events/AfterGameLoadedEvent";
import MemoryLogger from "@/modules/base/services/MemoryLogger";

type DebugOptionType = { noGames?: boolean, noCup?: boolean, noLeague?: boolean, noInternational?: boolean };
type TeamType = { name: string, shortName: string, primaryColor: string, secondaryColor: string };
export type GameOptionsType = { debug: DebugOptionType, gameSlotName: string, country: CountryEnum, team: TeamType };

@injectable()
export class GameCreator {
  @inject(EventEmitter)
  private em: EventEmitter;

  constructor(
    @inject(CountryCompetitionFactory) private countryCompetitionFactory: CountryCompetitionFactory,
    @inject(WorldFactory) private worldFactory: WorldFactory,
    @inject(TeamFactory) private teamFactory: TeamFactory,
    @inject(SettingRepository) private settingRepository: SettingRepository,
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @inject(FastPlayerFactory) private fastPlayerFactory: FastPlayerFactory,
    @inject(MemoryLogger) private memoryLogger: MemoryLogger,
  ) {
  }

  public async generate(options: GameOptionsType, loader: SectionLoader, leagueCount = 1, teamsPerLeagueCount = 18) {
    this.memoryLogger.log();

    await this.em.emitAsync(new BeforeGameLoadedEvent);

    const internationalCountries = this.configRepository.get('general.international.countries');
    const teamsPerCountry = this.configRepository.get('general.international.teamsPerCountry');

    loader.addSection('world');
    loader.addSection('user-team');
    loader.addSection('teams', leagueCount * teamsPerLeagueCount);
    loader.addSection('leagues');
    loader.addSection('cups');
    loader.addSection('international-teams', Object.keys(internationalCountries).length * teamsPerCountry);
    loader.addSection('wrapping-up');

    await loader.processSection('world', async () => {
      console.time('time::GameCreator::generate');
      await this.em.emitAsync(new BeforeCreateGameEvent());

      await this.settingRepository.set('game_id', chance.hash({length: 20}));
      await Season.unsetCurrent();
      const season = new Season();
      season.current = true;
      season.currentDate = season.startDate;
      season.currentDate.set('year', this.configRepository.get('general.seasonStartYear'));
      await season.save();

      await nonblocking(() => this.worldFactory.generate());
    });

    const country = await loader.processSection('user-team', async () => {
      const country = await Country
        .createQueryBuilder('country')
        .innerJoinAndSelect('country.region', 'region')
        .where('country.name = :countryName', {countryName: options.country})
        .getOneOrFail();

      await this.teamFactory.generate(country, 20, (team: Team) => {
        team.user = true;
        team.name = options.team.name;
        team.shortName = options.team.shortName;
        team.country = country;
        team.primaryColor = options.team.primaryColor;
        team.secondaryColor = options.team.secondaryColor;
      });

      return country;
    });

    if (!options.debug.noGames) {
      await nonblocking(() => this.countryCompetitionFactory.generate(country, leagueCount, teamsPerLeagueCount, options.debug, loader));
    }

    if (!options.debug.noInternational) {
      await this.generateInternationalTeams(loader, country, internationalCountries, teamsPerCountry);
    }

    await nonblocking(() => loader.processSection('wrapping-up', () => waitFor(1)));

    await this.em.emitAsync(new AfterCreateGameEvent());

    await persistDataSource(db);

    await this.em.emitAsync(new AfterGameLoadedEvent());
    console.timeEnd('time::GameCreator::generate');
  }

  private async generateInternationalTeams(
    loader: SectionLoader,
    country: Country,
    internationalContries: { [key in CountryEnum]?: number },
    teamsPerCountry: number
  ) {
    await loader.processSection('international-teams', async (advance: (step?: number) => void) => {
      const countries: Country[] = await Country
        .createQueryBuilder('country')
        .innerJoinAndSelect('country.region', 'region')
        .where('country.name != :countryName', {countryName: country.name})
        .getMany();

      await collect(internationalContries)
        .keys()
        .toArray<CountryEnum>()
        .filter((countryName: CountryEnum) => country.name !== countryName)
        .map((countryName: CountryEnum) => countries.find(c => c.name === countryName)!)
        .forEachAsync(async (country: Country) => {
          await nonblocking(async () => {
            await times(teamsPerCountry).forEachAsync(async (i: number) => {
              await this.teamFactory.generate(
                country,
                internationalContries[country.name]! - (teamsPerCountry - i) * 5
              );

              advance();
            });
          });
        });

      await this.fastPlayerFactory.flush();
    });
  }
}